import type {
  MessageActionRequest,
  MessageActionResponse,
  SearchMessageActionRequest,
  SearchMessageActionResponse,
} from '@email/shared';
import { toast } from 'sonner';
import { useMailAction, useSearchMailAction } from '../api/mail.js';
import { useUndoStore } from '../stores/undo.js';

export function useMailActionRunner() {
  const mutation = useMailAction();
  const searchMutation = useSearchMailAction();

  const executeUndo = async (undo: NonNullable<MessageActionResponse['undo']>) => {
    try {
      await mutation.undo(undo);
      useUndoStore.getState().forget(undo);
      toast.success('Undone');
      return true;
    } catch (error) {
      useUndoStore.getState().remember(undo);
      toast.error(actionError(error));
      return false;
    }
  };

  return {
    ...mutation,
    isPending: mutation.isPending || searchMutation.isPending,
    undo: executeUndo,
    run: async (
      request: MessageActionRequest,
      successMessage: string | null = null,
    ): Promise<MessageActionResponse> => {
      try {
        const result = await mutation.mutateAsync(request);
        if (result.undo && result.affectedMessageIds.length > 0) {
          useUndoStore.getState().remember(result.undo);
        }
        if (successMessage && result.affectedMessageIds.length > 0) {
          toast.success(successMessage, {
            duration: 8_000,
            ...(result.undo
              ? {
                  action: {
                    label: 'Undo',
                    onClick: () => {
                      void executeUndo(result.undo!);
                    },
                  },
                }
              : {}),
          });
        }
        return result;
      } catch (error) {
        toast.error(actionError(error));
        throw error;
      }
    },
    runSearch: async (
      request: SearchMessageActionRequest,
    ): Promise<SearchMessageActionResponse> => {
      try {
        const result = await searchMutation.mutateAsync(request);
        if (result.undo && result.affectedMessageIds.length > 0) {
          useUndoStore.getState().remember(result.undo);
        }
        const limitNote = result.hasMore
          ? ` The ${result.selectionLimit.toLocaleString()}-conversation safety limit was reached; narrow the search to target the remainder.`
          : '';
        if (result.affectedMessageIds.length === 0) {
          toast.info(
            result.selectedThreadCount === 0
              ? 'No conversations match this search now.'
              : `No matching conversations needed that change.${limitNote}`,
            { duration: 10_000 },
          );
        } else {
          toast.success(
            `Processed ${result.selectedThreadCount.toLocaleString()} matching ${
              result.selectedThreadCount === 1 ? 'conversation' : 'conversations'
            }.${limitNote}`,
            {
              duration: 12_000,
              ...(result.undo
                ? {
                    action: {
                      label: 'Undo',
                      onClick: () => {
                        void executeUndo(result.undo!);
                      },
                    },
                  }
                : {}),
            },
          );
        }
        return result;
      } catch (error) {
        toast.error(actionError(error));
        throw error;
      }
    },
    undoLast: async (): Promise<boolean> => {
      const undo = useUndoStore.getState().take();
      if (!undo) {
        toast.info('Nothing to undo.');
        return false;
      }
      return executeUndo(undo);
    },
  };
}

function actionError(error: unknown): string {
  if (!(error instanceof Error)) return 'Mail action failed.';
  switch (error.message) {
    case 'archive_folder_not_found':
      return 'This account does not have an Archive folder.';
    case 'trash_folder_not_found':
      return 'This account does not have a Trash folder.';
    case 'junk_folder_not_found':
      return 'This account does not have a Spam or Junk folder.';
    case 'inbox_folder_not_found':
      return 'This account does not have an Inbox folder.';
    case 'permanent_delete_requires_trash':
      return 'Messages can only be deleted forever from Trash.';
    case 'target_folder_account_mismatch':
      return 'A named folder can only receive conversations from its own account.';
    default:
      return error.message || 'Mail action failed.';
  }
}
